import React, { useState } from 'react';
import { 
  Globe, Download, Copy, Check, 
  AlertCircle, ArrowLeft, Sparkles, FileText, HelpCircle, Trees, Layers, Compass
} from 'lucide-react';

interface SatelliteEcoAnalystProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація аналітичного модуля космічного моніторингу та ГІС-аналізу...",
  "Отримання метаданих супутникових знімків (Sentinel-2 / Landsat 8-9 / Earth Engine)...",
  "Розрахунок спектральних індексів (NDVI, NBR, MNDWI) та просторовий аналіз...",
  "Оцінка екологічних збитків, деградації біомаси та ризиків довкіллю...",
  "Формування підсумкового аналітичного звіту космічного еко-моніторингу..."
];

const LOADING_STEPS_EN = [
  "Initializing Earth Observation AI agents & GIS workspace...",
  "Fetching satellite metadata (Sentinel-2 / Landsat 8-9 / Earth Engine)...",
  "Computing spectral indices (NDVI, NBR, MNDWI) and spatial shift analysis...",
  "Assessing biomass loss, environmental damages, and ecosystem risks...",
  "Generating final Satellite Eco Monitoring Audit Report..."
];

const PRESETS_UK = [
  {
    title: "Дністровське водосховище (Бакота)",
    type: "Критичне обміління та цвітіння (NDWI / MNDWI / 12-8-4)",
    region: "Бакота, Дністровський каньйон, Кам'янець-Подільський р-н (48.58° N, 27.00° E)",
    timeframe: "2018 - 2026 рр. (Вересневе порівняння маловоддя)",
    sources: "Sentinel-2 MSI (10м) + False Color 12-8-4 + GUM",
    context: "Виявлення оголення дна (>100 м відхід урізу води, -13.7% площі акваторії), оцінка евтрофікації та розрахунок бюджету невизначеності GUM."
  },
  {
    title: "Каховське водосховище та Дніпро",
    type: "Моніторинг водойм та обміління (MNDWI)",
    region: "Каховське водосховище, Херсонська/Запорізька обл., 46.8° N, 33.6° E",
    timeframe: "2023 - 2026 рр. (Порівняння до та після руйнування ГЕС)",
    sources: "Sentinel-2 (ESA) + Sentinel-1 SAR",
    context: "Оцінка зміни дзеркала води, мулистих відкладень, оновлення рослинного покриву на колишньому дні водосховища."
  },
  {
    title: "Карпатські лісові масиви",
    type: "Вирубка лісів та біомаса (NDVI)",
    region: "Карпатський національний природний парк, Яремче - Ворохта - Рахів",
    timeframe: "Динаміка за 2022 - 2026 рр.",
    sources: "Sentinel-2 MSI (10м роздільна здатність)",
    context: "Виявлення осередків суцільних та вибіркових вирубок, сукцесії лісу, ерозійних зсувів на схилах."
  },
  {
    title: "Чорнобильська зона відчуження",
    type: "Аналіз пожеж та згарищ (NBR)",
    region: "Чорнобильський радіаційно-екологічний біосферний заповідник",
    timeframe: "Сезон пожеж 2024 - 2026 рр.",
    sources: "Sentinel-2 SWIR + MODIS Thermal",
    context: "Оцінка площ випалених лісових масивів (индекс NBR/dNBR), ступеня термічного ураження та відновлення рослинності."
  },
  {
    title: "Полісся: Торфовища та Болота",
    type: "Опустелювання та деградація ґрунтів",
    region: "Житомирське та Рівненське Полісся",
    timeframe: "Багаторічна динаміка 2021 - 2026 рр.",
    sources: "Sentinel-2 + Landsat 9",
    context: "Моніторинг осушення торфовищ, виснаження підземних вод, ризиків підземних торф'яних пожеж."
  }
];

export default function SatelliteEcoAnalyst({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: SatelliteEcoAnalystProps) {
  const isUk = language === 'uk';
  const [regionName, setRegionName] = useState('Карпатські лісові масиви (Яремчанський регіон)');
  const [monitoringType, setMonitoringType] = useState('Вирубка лісів та аналіз біомаси (NDVI)');
  const [timeFrame, setTimeFrame] = useState('Динаміка за 2024 - 2026 рр.');
  const [satelliteSources, setSatelliteSources] = useState('Sentinel-2 (ESA MSI 10m) + Landsat 9');
  const [additionalContext, setAdditionalContext] = useState(
    useEnterpriseContext ? (isUk ? 'Враховувати лісові ділянки та земельні угіддя підприємства з бази знань.' : 'Include enterprise land plots from knowledge base.') : ''
  );

  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);

  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 7000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  const handleApplyPreset = (preset: typeof PRESETS_UK[0]) => {
    setRegionName(preset.region);
    setMonitoringType(preset.type);
    setTimeFrame(preset.timeframe);
    setSatelliteSources(preset.sources);
    setAdditionalContext(preset.context);
  };

  const handleRun = async () => {
    if (!regionName.trim()) return;

    setIsAuditing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: "Аналіз космічних знімків та екологічний моніторинг",
          agentId: "satellite-eco-analyst",
          inputs: {
            region_name: regionName,
            monitoring_type: monitoringType,
            time_frame: timeFrame,
            satellite_sources: satelliteSources,
            additional_context: additionalContext || (isUk ? "Стандартний космічний екологічний аналіз території" : "Standard satellite eco audit")
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка аналізу супутникових знімків.' : 'Failed to run satellite eco audit.'));
      }

      const data = await response.json();

      if (data.jobId) {
        const pollJob = async () => {
          const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
          if (!statusRes.ok) throw new Error(isUk ? 'Помилка перевірки статусу.' : 'Failed to check status.');
          const statusData = await statusRes.json();

          if (statusData.status === 'completed') {
            setReport(statusData.report);
            setIsAuditing(false);
          } else if (statusData.status === 'failed') {
            setError(statusData.error || (isUk ? 'Помилка під час виконання аналізу.' : 'Audit execution failed.'));
            setIsAuditing(false);
          } else {
            setTimeout(pollJob, 3000);
          }
        };
        setTimeout(pollJob, 3000);
      } else if (data.report) {
        setReport(data.report);
        setIsAuditing(false);
      } else {
        throw new Error(isUk ? 'Невідомий формат відповіді сервера.' : 'Invalid server response.');
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка при виконання аналізу.' : 'An error occurred during execution.'));
      setIsAuditing(false);
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  const handleDownloadDocx = async () => {
    if (!report) return;
    setIsDownloadingDocx(true);

    try {
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: report,
          fileName: `Satellite_Eco_Audit_${regionName.replace(/[^a-zA-Z0-9абвгґдеєжзиіїйклмнопрстуфхцчшщьюяАБВГҐДЕЄЖЗИІЇЙКЛМНОПРСТУФХЦЧШЩЬЮЯ_-]/g, '_').substring(0, 30)}`
        })
      });

      if (!response.ok) {
        throw new Error('Помилка генерації файлу Word (.docx).');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Satellite_Eco_Audit_${Date.now()}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message || 'Не вдалося завантажити Word документ.');
    } finally {
      setIsDownloadingDocx(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-emerald-900/30 pb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
            >
              🏠 {isUk ? "На головну" : "Home"}
            </button>
            <button 
              onClick={onBack}
              className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
            >
              <ArrowLeft className="w-4 h-4 text-emerald-400" />
              <span>{isUk ? "← До Космічного Центру" : "← Back to Space Hub"}</span>
            </button>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
                <Globe className="w-8 h-8 text-emerald-400" />
                {isUk ? 'Агент «Космічний Еко-Моніторинг»' : 'AI Satellite Eco Analyst'}
              </h1>
              <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                {isUk ? 'ЕКОЛОГІЯ & ДЗЗ' : 'EARTH OBSERVATION'}
              </span>
            </div>
            <p className="text-sm text-slate-400 mt-1">
              {isUk 
                ? 'Аналіз космічних знімків (Sentinel, Landsat), розрахунок індексів NDVI, NBR, MNDWI, аналіз вирубки лісів, пожеж та стану водойм' 
                : 'Satellite image analytics (Sentinel, Landsat), computing NDVI, NBR, MNDWI indices, monitoring deforestation, fires & water bodies'}
            </p>
          </div>
        </div>

        {useEnterpriseContext && enterpriseName && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-950/40 border border-emerald-800/40 rounded-lg text-emerald-300 text-xs">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span>{isUk ? `Контекст: ${enterpriseName}` : `Context: ${enterpriseName}`}</span>
          </div>
        )}
      </div>

      {/* Presets Selection */}
      <div className="space-y-3">
        <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-2">
          <Compass className="w-4 h-4 text-emerald-400" />
          {isUk ? 'Готові сценарії космічного спостереження (Клікніть для вибору)' : 'Preset Earth Observation Scenarios'}
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PRESETS_UK.map((preset, idx) => (
            <button
              key={idx}
              onClick={() => handleApplyPreset(preset)}
              className="text-left p-3.5 bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800 hover:border-emerald-500/40 rounded-xl transition group"
            >
              <div className="text-sm font-bold text-slate-200 group-hover:text-emerald-400 transition flex items-center justify-between">
                <span>{preset.title}</span>
                <Globe className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 transition" />
              </div>
              <div className="text-xs text-emerald-400/90 font-medium mt-1">
                {preset.type}
              </div>
              <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">
                {preset.region}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Form Input Section */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 space-y-6 shadow-xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Region / Coordinates */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-200 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-emerald-400" />
                {isUk ? 'Регіон / Координати / Орієнтири територій' : 'Region / Coordinates / Area Landmarks'}
              </span>
              <span className="text-[10px] text-emerald-400 font-mono">📋 {isUk ? 'оберіть зі списку' : 'select from list'}</span>
            </label>
            <select
              onChange={(e) => {
                if (e.target.value) setRegionName(e.target.value);
              }}
              className="w-full mb-1 px-3 py-2 bg-slate-950 border border-slate-700/80 rounded-xl text-slate-300 text-xs font-mono focus:outline-none focus:border-emerald-500 cursor-pointer"
            >
              <option value="">{isUk ? '🔽 -- Швидкий вибір цільового регіону --' : '🔽 -- Quick Select Target Region --'}</option>
              <option value="Карпатський національний природний парк, 48.15° N, 24.50° E">🌲 {isUk ? 'Карпатський лісовий масив' : 'Carpathian Forest Massif'}</option>
              <option value="Каховське водосховище та Дніпровські плавні, 46.8° N, 33.6° E">🌊 {isUk ? 'Каховське водосховище та Дніпро' : 'Kakhovka Reservoir & Dnipro'}</option>
              <option value="Чорнобильська зона відчуження, 51.27° N, 30.22° E">☢️ {isUk ? 'Чорнобильська зона відчуження' : 'Chornobyl Exclusion Zone'}</option>
              <option value="Аграрний сектор Півдня України (Херсонська/Миколаївська обл.)">🌾 {isUk ? 'Аграрний сектор Півдня України' : 'South Ukraine Agriculture'}</option>
              <option value="Акваторія Чорного та Азовського морів">🌊 {isUk ? 'Акваторія Чорного та Азовського морів' : 'Black Sea & Sea of Azov'}</option>
            </select>
            <input
              type="text"
              value={regionName}
              onChange={(e) => setRegionName(e.target.value)}
              placeholder={isUk ? "напр., Каховське водосховище, 46.8° N, 33.6° E" : "e.g., Kakhovka Reservoir, 46.8 N, 33.6 E"}
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700/80 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition text-sm"
            />
          </div>

          {/* Type of Monitoring */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-200 flex items-center gap-2">
              <Layers className="w-4 h-4 text-emerald-400" />
              {isUk ? 'Тип екологічного моніторингу' : 'Environmental Monitoring Focus'}
            </label>
            <select
              value={monitoringType}
              onChange={(e) => setMonitoringType(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700/80 rounded-xl text-slate-100 focus:outline-none focus:border-emerald-500 transition text-sm"
            >
              <option value="Вирубка лісів та аналіз біомаси (NDVI)">
                {isUk ? 'Вирубка лісів та стан біомаси (NDVI)' : 'Deforestation & Vegetation Biomass (NDVI)'}
              </option>
              <option value="Моніторинг пожеж та обпалених площ (NBR)">
                {isUk ? 'Моніторинг пожеж та обпалених площ (NBR / dNBR)' : 'Wildfire Burned Area Analysis (NBR / dNBR)'}
              </option>
              <option value="Стан водойм та обміління (MNDWI)">
                {isUk ? 'Моніторинг водойм та обміління (MNDWI)' : 'Water Bodies & Drought Shrinkage (MNDWI)'}
              </option>
              <option value="Опустелювання та деградація ґрунтів">
                {isUk ? 'Опустелювання, зсуви та деградація ґрунтів' : 'Soil Erosion & Desertification Analysis'}
              </option>
              <option value="Комплексний екологічний аудит території">
                {isUk ? 'Комплексний супутниковий еко-аудит (NDVI + NBR + MNDWI)' : 'Comprehensive Satellite Eco Audit (NDVI + NBR + MNDWI)'}
              </option>
            </select>
          </div>

          {/* Timeframe */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-200 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-emerald-400" />
                {isUk ? 'Часовий інтервал / Охоплений період' : 'Timeframe / Covered Period'}
              </span>
              <span className="text-[10px] text-emerald-400 font-mono">📅 {isUk ? 'оберіть період' : 'select timeframe'}</span>
            </label>
            <select
              onChange={(e) => {
                if (e.target.value) setTimeFrame(e.target.value);
              }}
              className="w-full mb-1 px-3 py-2 bg-slate-950 border border-slate-700/80 rounded-xl text-slate-300 text-xs font-mono focus:outline-none focus:border-emerald-500 cursor-pointer"
            >
              <option value="">{isUk ? '🔽 -- Швидкий вибір часового інтервалу --' : '🔽 -- Quick Select Timeframe --'}</option>
              <option value="Оперативний моніторинг (Останні 24 години)">⚡ {isUk ? 'Оперативний моніторинг (24 год)' : 'Real-time (24 Hours)'}</option>
              <option value="Добовий та тижневий зріз (Останні 7 днів)">📅 {isUk ? 'Тижневий зріз (7 днів)' : 'Weekly (7 Days)'}</option>
              <option value="Місячний динамічний просеріал (Останні 30 днів)">🗓️ {isUk ? 'Місячна динаміка (30 днів)' : 'Monthly (30 Days)'}</option>
              <option value="Сезонний порівняльний аналіз (2025 – 2026 рр.)">🌿 {isUk ? 'Сезонний аналіз (2025–2026)' : 'Seasonal (2025–2026)'}</option>
              <option value="Багаторічний ретроспективний архів (2020 – 2026 рр.)">📜 {isUk ? 'Багаторічний архів (2020–2026)' : 'Multi-year (2020–2026)'}</option>
            </select>
            <input
              type="text"
              value={timeFrame}
              onChange={(e) => setTimeFrame(e.target.value)}
              placeholder={isUk ? "напр., Динаміка за 2024 - 2026 рр." : "e.g., Dynamics for 2024 - 2026"}
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700/80 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition text-sm"
            />
          </div>

          {/* Satellite Constellations */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-200 flex items-center gap-2">
              <Trees className="w-4 h-4 text-emerald-400" />
              {isUk ? 'Супутникові сузір\'я та ДЗЗ джерела' : 'Satellite Constellations & Sensors'}
            </label>
            <select
              value={satelliteSources}
              onChange={(e) => setSatelliteSources(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700/80 rounded-xl text-slate-100 focus:outline-none focus:border-emerald-500 transition text-sm"
            >
              <option value="Sentinel-2 (ESA MSI 10m) + Landsat 9">
                Sentinel-2 (ESA) + Landsat 9 (NASA/USGS)
              </option>
              <option value="Sentinel-2 MSI (Мультиспектральна зйомка 10м)">
                Sentinel-2 MSI (Multispectral 10m resolution)
              </option>
              <option value="Sentinel-1 SAR (Радіолокаційна зйомка будь-якої погоди)">
                Sentinel-1 SAR (All-weather Radar)
              </option>
              <option value="Комплексні джерела (Sentinel-1/2 + Landsat 8/9 + MODIS)">
                Multi-Sensor Fusion (Sentinel-1/2 + Landsat 8/9 + MODIS)
              </option>
            </select>
          </div>
        </div>

        {/* Additional Context */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-200 flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-emerald-400" />
            {isUk ? 'Додаткові технічні вимоги або фактори аналізу' : 'Additional Technical Details / Context'}
          </label>
          <textarea
            rows={3}
            value={additionalContext}
            onChange={(e) => setAdditionalContext(e.target.value)}
            placeholder={isUk ? "Вкажіть специфічні об'єкти, межі кадастрових ділянок чи особливі екологічні задачі..." : "Specify specific sites, land boundaries, or ecological priorities..."}
            className="w-full px-4 py-3 bg-slate-950 border border-slate-700/80 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition text-sm resize-none"
          />
        </div>

        {/* Action Button */}
        <button
          onClick={handleRun}
          disabled={isAuditing || !regionName.trim()}
          className="w-full py-3.5 px-6 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-500 hover:from-emerald-500 hover:to-teal-400 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
        >
          {isAuditing ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>{isUk ? 'Виконується космічний моніторинг...' : 'Processing satellite imagery...'}</span>
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5 text-emerald-200" />
              <span>{isUk ? 'Запустити Космічний Еко-Моніторинг' : 'Launch Satellite Eco Monitoring'}</span>
            </>
          )}
        </button>
      </div>

      {/* Progress Loader State */}
      {isAuditing && (
        <div className="bg-slate-900/90 border border-emerald-500/30 rounded-2xl p-6 space-y-4 text-center animate-pulse shadow-xl">
          <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-400 rounded-full animate-spin mx-auto" />
          <h3 className="text-lg font-semibold text-white">
            {isUk ? 'Триває мультиспектральний аналіз ДЗЗ...' : 'Multispectral Earth Observation in Progress...'}
          </h3>
          <p className="text-sm text-emerald-400 font-medium">
            {(isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN)[loadingStepIndex]}
          </p>
          <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden max-w-md mx-auto">
            <div 
              className="bg-emerald-500 h-full transition-all duration-500 ease-out"
              style={{ width: `${((loadingStepIndex + 1) / (isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN).length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="p-4 bg-red-950/40 border border-red-800/50 rounded-xl flex items-start gap-3 text-red-200 text-sm">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-300">{isUk ? 'Помилка виконання' : 'Execution Error'}</h4>
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Results view */}
      {report && !isAuditing && (
        <div className="bg-slate-900/90 border border-emerald-500/30 rounded-2xl p-6 md:p-8 space-y-6 shadow-2xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                <Globe className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {isUk ? 'Аналітичний Звіт Космічного Еко-Моніторингу' : 'Satellite Eco Monitoring Audit Report'}
                </h3>
                <p className="text-xs text-slate-400">
                  {isUk ? `Регіон: ${regionName} | Джерело: ${satelliteSources}` : `Region: ${regionName} | Sensors: ${satelliteSources}`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyReport}
                className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-semibold transition border border-slate-700 flex items-center gap-1.5"
              >
                {copiedReport ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span>{isUk ? 'Скопійовано' : 'Copied'}</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 text-slate-400" />
                    <span>{isUk ? 'Копіювати' : 'Copy'}</span>
                  </>
                )}
              </button>

              <button
                onClick={handleDownloadDocx}
                disabled={isDownloadingDocx}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-semibold transition shadow-md flex items-center gap-1.5 disabled:opacity-50"
              >
                {isDownloadingDocx ? (
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>{isUk ? 'Завантажити Word (.docx)' : 'Download Word (.docx)'}</span>
              </button>
            </div>
          </div>

          {/* Render Markdown text cleanly */}
          <div className="prose prose-invert max-w-none prose-emerald prose-headings:text-white prose-p:text-slate-300 prose-li:text-slate-300 prose-strong:text-emerald-300 text-sm leading-relaxed whitespace-pre-wrap font-sans">
            {report}
          </div>
        </div>
      )}
    </div>
  );
}
